import { CHARTV2_HORIZON_SPAN_MS } from '../constants';
import type { ChartV2Horizon, ChartV2Series } from '../types/chart-v2.types';
import { horizonToDomain } from './helpers';

const DAY_MS = 1000 * 60 * 60 * 24;

export interface DataExtent {
  start: number;
  end: number;
}

export const computeDataExtent = (series: ChartV2Series[]): DataExtent => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (const s of series) {
    for (const p of s.data) {
      if (p.time < min) {
        min = p.time;
      }

      if (p.time > max) {
        max = p.time;
      }
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    const now = Date.now();

    return { start: now - DAY_MS, end: now };
  }

  return { start: min, end: max };
};

/**
 * The "pannable" extent — a bit of space is reserved past the last data point
 * so the user can pan into the future.
 */
export const computePanExtent = (dataExtent: DataExtent): DataExtent => {
  const dataSpan = dataExtent.end - dataExtent.start;
  const futureBuffer = Math.max(dataSpan * 0.3, DAY_MS * 30);

  return { start: dataExtent.start, end: dataExtent.end + futureBuffer };
};

export const computeDataDisabledHorizons = (
  dataExtent: DataExtent,
  horizons: ChartV2Horizon[]
): ChartV2Horizon[] => {
  const inclusiveDataSpan = dataExtent.end - dataExtent.start + DAY_MS;

  return horizons.filter(horizon => {
    const horizonSpan = CHARTV2_HORIZON_SPAN_MS[horizon];

    return horizonSpan !== null && horizonSpan > inclusiveDataSpan;
  });
};

/**
 * Pads the horizon-based domain with a small amount of future space so the
 * latest data point doesn't sit flush against the y-axis on load.
 *
 * The pad only makes sense when the user can pan into that reserved future
 * space. When panning is disabled the latest point should sit flush against
 * the right edge.
 */
export const computeInitialDomain = (
  horizon: ChartV2Horizon,
  dataExtent: DataExtent,
  padFuture = true
): DataExtent => {
  const base = horizonToDomain(horizon, dataExtent.end, dataExtent.start);

  if (!padFuture) {
    return base;
  }

  const span = base.end - base.start;
  const futurePad = span * 0.15;

  return { start: base.start, end: base.end + futurePad };
};
